import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { ArrowUpRight, TriangleAlert, Users, CheckCircle2, FileClock, Activity as ActivityIcon, Sparkles, WifiOff, RotateCw } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import AttentionCenter from "@/components/dashboard/AttentionCenter";

export default function Dashboard() {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const { data } = await api.get("/dashboard/summary");
            setSummary(data);
        } catch (err) {
            setLoadError(err);
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (loadError && !summary) {
        return (
            <AppShell breadcrumbs={["Dashboard"]} onClientCreated={load}>
                <InlineLoadError title="We couldn't load your dashboard right now" onRetry={load} testid="dashboard-load-error" />
            </AppShell>
        );
    }

    return (
        <AppShell breadcrumbs={["Dashboard"]} onClientCreated={load}>
            <div className="max-w-7xl">
                <div className="flex items-end justify-between gap-6 mb-8">
                    <div className="fade-in">
                        <p className="overline mb-2">Overview</p>
                        <h1 className="font-display text-4xl tracking-tight text-slate-900">Good to see you</h1>
                        <p className="text-slate-500 mt-2 max-w-lg">Here's what's happening across your client portals today.</p>
                    </div>
                </div>

                {/* KPI bento */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 stagger">
                    <KPI title="Active clients" value={loading ? "" : summary?.active_clients} icon={Users}
                        subtitle={loading ? "" : `${summary?.total_clients || 0} total`} testid="kpi-active" />
                    <KPI title="Needing attention" value={loading ? "" : summary?.needs_attention} icon={TriangleAlert}
                        subtitle="Awaiting your input" tone="amber" testid="kpi-attention" />
                    <KPI title="Pending approvals" value={loading ? "" : summary?.pending_approvals} icon={FileClock}
                        subtitle="Across all portals" testid="kpi-approvals" />
                    <KPI title="Deliverables" value={loading ? "" : summary?.total_deliverables} icon={CheckCircle2}
                        subtitle="Live in portals" testid="kpi-deliverables" />
                </div>

                {/* AI P0 — Attention Center (deterministic intelligence) */}
                <AttentionCenter />

                {/* Attention + Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                    <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-6">
                        <div className="flex items-center justify-between mb-5">
                            <div>
                                <p className="overline mb-1">Client overview</p>
                                <h2 className="font-display text-xl text-slate-900">Your clients</h2>
                            </div>
                            <button onClick={() => navigate("/clients")} data-testid="see-all-clients"
                                className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                                See all <ArrowUpRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        {loading ? (
                            <div className="space-y-3">{[1,2,3,4].map(i => <div key={i} className="skeleton h-14" />)}</div>
                        ) : summary?.clients_overview?.length ? (
                            <div className="divide-y divide-slate-100">
                                {summary.clients_overview.slice(0, 6).map((c) => {
                                    const healthStatus = c?.email_health?.status || "healthy";
                                    const undeliverable = healthStatus === "undeliverable";
                                    const warning = healthStatus === "warning";
                                    return (
                                        <button key={c.client_id} onClick={() => navigate(`/clients/${c.client_id}`)}
                                            data-testid={`client-row-${c.client_id}`}
                                            className="w-full text-left flex items-center gap-4 py-4 hover:bg-slate-50 -mx-2 px-2 rounded-lg transition-colors">
                                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-600 relative">
                                                {c.company_name.split(" ").map(w => w[0]).slice(0,2).join("")}
                                                {(undeliverable || warning) && (
                                                    <span
                                                        data-testid={`dashboard-client-health-dot-${c.client_id}`}
                                                        aria-label={undeliverable ? "Email undeliverable" : "Delivery warning"}
                                                        className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-white ${
                                                            undeliverable ? "bg-rose-500" : "bg-amber-500"
                                                        }`} />
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-slate-900 truncate">{c.company_name}</span>
                                                    {undeliverable && (
                                                        <span data-testid={`dashboard-client-health-pill-${c.client_id}`}
                                                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">Email undeliverable</span>
                                                    )}
                                                    {!undeliverable && warning && (
                                                        <span data-testid={`dashboard-client-health-pill-${c.client_id}`}
                                                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Delivery warning</span>
                                                    )}
                                                    {!undeliverable && !warning && c.needs_attention && (
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Attention</span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-slate-500 truncate">{c.project_name}</div>
                                            </div>
                                            <div className="hidden sm:block w-32">
                                                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                                    <div className="h-full bg-[#C55A46]" style={{ width: `${c.progress}%` }} />
                                                </div>
                                                <div className="text-[10px] text-slate-400 mt-1 text-right">{c.progress}%</div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <EmptyState onCreate={() => document.querySelector('[data-testid="header-add-client-btn"]')?.click()} />
                        )}
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="recent-activity">
                        <div className="flex items-center gap-2 mb-5">
                            <ActivityIcon className="w-4 h-4 text-slate-500" />
                            <h2 className="font-display text-xl text-slate-900">Recent activity</h2>
                        </div>
                        {loading ? (
                            <div className="space-y-3">{[1,2,3,4].map(i => <div key={i} className="skeleton h-10" />)}</div>
                        ) : (
                            <ul className="space-y-4 stagger">
                                {(summary?.recent_activity || []).slice(0, 8).map((a, idx) => (
                                    <li key={a.activity_id} data-testid={`recent-activity-item-${idx}`} className="flex gap-3">
                                        <div className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${
                                            a.kind === "approval_pending" ? "bg-amber-500" :
                                            a.kind === "client_created" ? "bg-[#C55A46]" :
                                            a.kind === "milestone" ? "bg-emerald-500" : "bg-slate-400"
                                        }`} />
                                        <div className="min-w-0">
                                            <p className="text-sm text-slate-700 leading-snug">{a.message}</p>
                                            <p className="text-xs text-slate-400 mt-0.5">
                                                {formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}
                                            </p>
                                        </div>
                                    </li>
                                ))}
                                {!summary?.recent_activity?.length && (
                                    <li className="text-sm text-slate-500">No activity yet.</li>
                                )}
                            </ul>
                        )}
                    </div>
                </div>

                {/* AI teaser strip */}
                <div className="mt-4 rounded-xl border border-slate-200 bg-white p-6 flex items-center gap-4 hover-lift">
                    <div className="w-10 h-10 rounded-lg bg-[#FEF2F2] flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-[#C55A46]" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-display text-lg text-slate-900">AI weekly updates — coming soon</h3>
                        <p className="text-sm text-slate-500">Auto-draft client updates from portal activity. You approve, we send.</p>
                    </div>
                    <button onClick={() => navigate("/ai-updates")} className="text-sm font-medium text-slate-700 hover:text-slate-900" data-testid="ai-teaser-cta">
                        Learn more →
                    </button>
                </div>
            </div>
        </AppShell>
    );
}

function InlineLoadError({ title, onRetry, testid }) {
    return (
        <div className="max-w-7xl">
            <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50/70 p-6 flex items-start gap-4"
                data-testid={testid}>
                <div className="mt-0.5 shrink-0 w-10 h-10 rounded-full bg-white border border-amber-200 flex items-center justify-center">
                    <WifiOff className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <h2 className="font-display text-lg text-slate-900">{title}</h2>
                    <p className="text-sm text-slate-600 mt-1 max-w-lg">
                        The connection blipped for a moment. Your data is safe — nothing was changed.
                    </p>
                    <button onClick={onRetry} data-testid={`${testid}-retry`}
                        className="mt-4 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium bg-white border border-slate-200 hover:border-slate-300 text-slate-700 transition">
                        <RotateCw className="w-3.5 h-3.5" /> Retry
                    </button>
                </div>
            </div>
        </div>
    );
}


function KPI({ title, value, subtitle, icon: Icon, tone = "neutral", testid }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-6 hover-lift" data-testid={testid}>
            <div className="flex items-center justify-between">
                <p className="overline">{title}</p>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    tone === "amber" ? "bg-amber-50 text-amber-600" : "bg-slate-50 text-slate-500"
                }`}>
                    <Icon className="w-4 h-4" />
                </div>
            </div>
            <div className="mt-4 font-display text-4xl tracking-tight text-slate-900">
                {value === "" ? <span className="skeleton inline-block w-16 h-8" /> : (value ?? 0)}
            </div>
            <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
        </div>
    );
}

function EmptyState({ onCreate }) {
    return (
        <div className="text-center py-10">
            <p className="text-slate-500 mb-4">No clients yet. Add your first one to see the magic.</p>
            <button onClick={onCreate} className="btn-terra px-4 py-2 rounded-lg text-sm font-medium">Add your first client</button>
        </div>
    );
}
